import React from 'react';
import {useTranslation} from 'react-i18next';
import {useQuery} from '@tanstack/react-query';
import {DEFAULT_PLAYER_LABELS, LOCALE_LABEL_ITEMS, PlayerLabel} from '../../../../../incase-domain/playerLabel';
import {fetchLabelPresets} from '../../../../../service/presentationLabelPreset.service';
import {QUERY_KEYS} from '../../../../../utils/queryKeys';
import Select, {Option} from '../../../../components/common/Select';

interface LocaleSettingsProps {
    labels: PlayerLabel[];
    onChange: (labels: PlayerLabel[]) => void;
}

function mergeWithDefaults(saved: PlayerLabel[]): PlayerLabel[] {
    return DEFAULT_PLAYER_LABELS.map((def) => {
        const override = saved.find((l) => l.code === def.code);
        return override ?? ({...def, _id: def.code} as PlayerLabel);
    });
}

export function initLocaleLabels(saved?: PlayerLabel[]): PlayerLabel[] {
    return mergeWithDefaults(saved ?? []);
}

function LocaleSettings({labels, onChange}: LocaleSettingsProps): JSX.Element {
    const {t} = useTranslation();

    const {data: presets} = useQuery(QUERY_KEYS.labelPreset.lists(), () => fetchLabelPresets());

    const handleChange = (code: string, value: string): void => {
        const updated = labels.map((label) => (label.code === code ? {...label, en: value, hr: value} : label));
        onChange(updated);
    };

    const applyPreset = (option: Option | null): void => {
        if (!option) return;
        const preset = presets?.find((p) => p._id === option.value);
        if (!preset) return;
        onChange(mergeWithDefaults(preset.labels));
    };

    const presetOptions: Option[] = (presets ?? []).map((p) => ({value: p._id, label: p.name}));

    return (
        <div className="u-p-m">
            <div className="c-verticalNavigation__header">
                <div className="c-verticalNavigation__header_title">{t('presEditPage.settings.locale.title')}</div>
            </div>

            <p className="u-text-sm u-mb-3" style={{color: 'var(--color-text-secondary)'}}>
                {t('presEditPage.settings.locale.description')}
            </p>

            {presets && presets.length > 0 && (
                <div className="u-flex u-h-right u-mb-3">
                    <div style={{width: '240px'}}>
                        <Select
                            options={presetOptions}
                            placeholder={t('presEditPage.settings.locale.selectPreset')}
                            onChange={applyPreset}
                            value={undefined}
                            menuPlacement="auto"
                            isMenuUsingPortal
                        />
                    </div>
                </div>
            )}

            <div className="u-flex u-flex-col u-gap-1">
                {LOCALE_LABEL_ITEMS.map(({code, nameKey}) => {
                    const label = labels.find((l) => l.code === code);
                    if (!label) return null;

                    return (
                        <div key={code} className="c-settingsItem">
                            <div className="u-flex u-h-between u-v-center u-gap-4">
                                <p className="c-settingsItem__title">{t(nameKey as never)}</p>
                                <div className="c-sidepanel__input" style={{flex: '0 0 180px'}}>
                                    <input
                                        type="text"
                                        value={label.en}
                                        onChange={(e) => handleChange(code, e.target.value)}
                                        aria-label={t(nameKey as never)}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default LocaleSettings;
